// src/pages/CreditoManualPontos.js

import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import ModalSemPermissao from "../components/ModalSemPermissao";
import { CheckCircle2, Coins, Save, Search, ShieldAlert } from "lucide-react";

import "./CreditoManualPontos.css";

function CreditoManualPontos() {
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState("");
  const [pontos, setPontos] = useState("");
  const [motivo, setMotivo] = useState("");
  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [clienteTexto, setClienteTexto] = useState("");

  useEffect(() => {
    axios
      .get("/clientes")
      .then((res) => setUsuarios(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error("Erro ao buscar usuários:", err);
        setErro("Erro ao carregar usuários.");
      });
  }, []);

  useEffect(() => {
    const usuarioSelecionado = usuarios.find(
      (u) =>
        `${u.nome} (${u.email})`.toLowerCase() === clienteTexto.toLowerCase()
    );

    if (usuarioSelecionado) {
      setUsuarioId(usuarioSelecionado.id.toString());
    }
  }, [clienteTexto, usuarios]);

  const usuarioSelecionado = useMemo(
    () => usuarios.find((u) => String(u.id) === String(usuarioId)),
    [usuarios, usuarioId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErro("");
    setMensagem("");

    const pontosInt = parseInt(pontos, 10);

    if (!usuarioId || !pontosInt || !motivo.trim()) {
      setErro("Preencha todos os campos corretamente.");
      return;
    }

    try {
      setEnviando(true);

      await axios.post("/creditar-pontos", {
        usuario_id: usuarioId,
        pontos: pontosInt,
        motivo,
      });

      setMensagem("Pontos creditados com sucesso!");
      setPontos("");
      setMotivo("");
      setUsuarioId("");
      setClienteTexto("");
    } catch (err) {
      console.error("Erro ao creditar pontos:", err);
      setErro("Erro ao creditar pontos.");
    } finally {
      setEnviando(false);
    }
  };

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  const podeCreditar =
    usuario?.tipo === "admin" ||
    usuario?.permissoes?.some(
      (p) => p.rota === "credito-manual" && p.pode_inserir
    );

  if (!podeCreditar) {
    return <ModalSemPermissao onClose={() => (window.location.href = "/")} />;
  }

  return (
    <div className="creditoManualAdmin-page">
      <div className="creditoManualAdmin-topbar">
        <div>
          <span className="creditoManualAdmin-kicker">Pontuação</span>
          <h2>Creditar Pontos Manualmente</h2>
          <p>
            Lance pontos manualmente para um cliente, informando a quantidade e
            o motivo do bônus.
          </p>
        </div>
      </div>

      {mensagem && (
        <div className="creditoManualAdmin-alert success">
          <CheckCircle2 size={16} />
          {mensagem}
        </div>
      )}

      {erro && <div className="creditoManualAdmin-alert danger">{erro}</div>}

      <section className="creditoManualAdmin-card">
        <div className="creditoManualAdmin-cardHeader">
          <div>
            <h3>Novo crédito manual</h3>
            <span>Esta ação altera diretamente o saldo de pontos do cliente.</span>
          </div>

          <Coins size={18} />
        </div>

        <form className="creditoManualAdmin-form" onSubmit={handleSubmit}>
          <div className="creditoManualAdmin-warningBox">
            <ShieldAlert size={18} />
            <div>
              <strong>Ação administrativa sensível</strong>
              <span>
                Confira o cliente, a quantidade de pontos e o motivo antes de
                confirmar o crédito.
              </span>
            </div>
          </div>

          <div className="creditoManualAdmin-grid">
            <div className="creditoManualAdmin-field creditoManualAdmin-fieldFull">
              <label>Cliente</label>

              <div className="creditoManualAdmin-searchInput">
                <Search size={15} />
                <input
                  type="text"
                  placeholder="Digite nome ou e-mail"
                  value={clienteTexto}
                  onChange={(e) => {
                    setClienteTexto(e.target.value);
                    setUsuarioId("");
                  }}
                  list="lista-clientes"
                />
              </div>

              <datalist id="lista-clientes">
                {usuarios
                  .filter((u) =>
                    `${u.nome} ${u.email}`
                      .toLowerCase()
                      .includes(clienteTexto.toLowerCase())
                  )
                  .map((u) => (
                    <option
                      key={u.id}
                      value={`${u.nome} (${u.email})`}
                      data-id={u.id}
                    />
                  ))}
              </datalist>
            </div>

            <div className="creditoManualAdmin-field">
              <label>Quantidade de pontos</label>
              <input
                type="number"
                min="1"
                value={pontos}
                onChange={(e) => setPontos(e.target.value)}
                placeholder="Ex: 1000"
              />
            </div>

            <div className="creditoManualAdmin-resumoCliente">
              <span>Cliente selecionado</span>
              <strong>{usuarioSelecionado?.nome || "Nenhum cliente selecionado"}</strong>
              <small>{usuarioSelecionado?.email || "Selecione pela busca acima"}</small>
            </div>

            <div className="creditoManualAdmin-field creditoManualAdmin-fieldFull">
              <label>Motivo do bônus</label>
              <textarea
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Descreva o motivo do crédito manual"
              />
            </div>
          </div>

          <div className="creditoManualAdmin-submitBar">
            <button
              type="submit"
              className="creditoManualAdmin-btn primary"
              disabled={enviando}
            >
              <Save size={16} />
              {enviando ? "Salvando..." : "Creditar pontos"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default CreditoManualPontos;